{
  "nav": { "items": { "myPage": "My page" } },
  "myPage": {
    "subtitle": "Your public page on {{brand}}: pick a template, arrange the sections and publish.",
    "loading": "Preparing your page",
    "loadFailed": "Your page could not be loaded",
    "railLabel": "Page settings",
    "markdownHint": "Simple formatting is supported: # heading, **bold**, lists with - and links as [text](https://…). HTML is not accepted.",
    "markdownHintShort": "**Bold**, lists with - and links are supported.",
    "tabs": { "label": "My page sections", "builder": "Edit page", "leads": "Page requests" },
    "status": {
      "draft": "Draft",
      "published": "Published",
      "unpublished": "Unpublished",
      "suspended": "Suspended",
      "unknown": "Unknown"
    },
    "statusCard": {
      "title": "Page status",
      "liveUrl": "Your page address",
      "futureUrl": "Once published, the page will be available at this address",
      "copy": "Copy address",
      "copied": "Address copied",
      "copyFailed": "The address could not be copied",
      "lastPublished": "Last published: {{date}}",
      "preview": "Preview",
      "openPreview": "Open preview",
      "popupBlocked": "The browser blocked the preview window; use the link below.",
      "publish": "Publish",
      "republish": "Publish changes",
      "unpublish": "Unpublish",
      "unpublishTitle": "Unpublish the page?",
      "unpublishBody": "The page is taken off the internet and its address stops opening. All content is kept and you can publish again.",
      "unpublishConfirm": "Unpublish",
      "unpublished": "The page was unpublished",
      "unpublishFailed": "Unpublishing failed. Try again.",
      "blockersTitle": "To publish, complete the following:",
      "addCouncil": "Add your medical council number in your profile"
    },
    "banner": {
      "suspendedTitle": "Your page has been suspended by {{brand}}",
      "suspendedNoReason": "No reason was recorded.",
      "suspendedSupport": "The page is read-only. Contact {{brand}} support to lift the suspension.",
      "unpublishedChanges": "You have changes that are not published yet; visitors see the previous version."
    },
    "save": {
      "save": "Save",
      "reset": "Reset",
      "saving": "Saving…",
      "saved": "Saved",
      "dirty": "Unsaved changes"
    },
    "publish": {
      "title": "Publish page",
      "intro": "Once published, your page can be seen and searched by everyone on the internet. Before publishing, confirm the following:",
      "rule1": "The content follows the Medical Council's advertising rules and makes no claim of guaranteed cure, guaranteed results or comparison with colleagues.",
      "rule2": "The images and texts are yours, or you have permission to use them.",
      "rule3": "Patient testimonials are published with the patients' consent, and the page holds no personal medical information.",
      "rule4": "You are responsible for the page's content, and {{brand}} may suspend a page that does not follow these rules.",
      "public": "The page is public: anyone with its address, and search engines, can see it.",
      "accept": "I have read and accept these terms",
      "confirm": "Publish",
      "cancel": "Cancel",
      "failedTitle": "Publishing failed; fix the following",
      "done": "Your page was published"
    },
    "template": { "title": "Template" },
    "templates": {
      "classic": { "name": "Classic", "description": "Print-like and composed: a framed portrait, a large title with a marked second half, the background image as a band across the top, and a bar of highlights." },
      "modern": { "name": "Modern", "description": "Like a clinic's landing page: a large title, a background image, cards beside your photo and a strip of highlights." },
      "minimal": { "name": "Minimal", "description": "Typographic and airy, no cover; focused on text, services and articles." }
    },
    "palettes": {
      "azure": "Azure",
      "teal": "Turquoise",
      "deep": "Navy",
      "brand": "{{brand}} brand",
      "cyan": "Cyan",
      "ink": "Ink"
    },
    "theme": {
      "title": "Colour and density",
      "palette": "Palette",
      "density": "Density",
      "densities": { "comfortable": "Comfortable", "compact": "Compact" },
      "onlyTemplate": "Only the palettes and densities the current template supports are shown."
    },
    "slug": {
      "label": "Page address",
      "hint": "Lower-case English letters, digits and hyphens; 3 to 40 characters. The default is your medical council number.",
      "lockedHint": "The address was fixed at the first publish and can no longer change.",
      "required": "Enter an address.",
      "invalid": "Only lower-case English letters, digits and hyphens, 3 to 40 characters; it must start and end with a letter or digit, with no double hyphens.",
      "checking": "Checking…",
      "available": "This address is available.",
      "taken": "This address is not available.",
      "save": "Save address"
    },
    "sections": {
      "title": "Sections",
      "reorderHint": "Change the order with the up and down buttons (or Alt and the arrow keys).",
      "kinds": {
        "hero": "Professional profile",
        "services": "Services",
        "articles": "Articles",
        "testimonials": "Selected patient testimonials",
        "gallery": "Image gallery",
        "video": "Video",
        "office": "Office information",
        "booking": "Booking"
      },
      "show": "Show “{{name}}” on the page",
      "shown": "Shown",
      "hidden": "Hidden",
      "hiddenNotice": "This section is hidden and does not appear on your public page.",
      "showOnPage": "Show on page",
      "toggleFailed": "Could not change whether «{{name}}» is shown:",
      "required": "This section is always shown",
      "moveUp": "Move “{{name}}” up",
      "moveDown": "Move “{{name}}” down",
      "unsupported": "Not shown in this template",
      "unsupportedLong": "The current template does not show this section. Its content is kept and appears again if you change template.",
      "hasErrors": "This section is blocking publication",
      "titleOverride": "Section heading (optional)",
      "titleOverrideHint": "Leave empty to show the default heading.",
      "reorderFailed": "The section order was not saved. Try again.",
      "booking": {
        "intro": "A visitor can pick a free time at one of your clinics or just ask to be called back. Whether a service is public and directly bookable is the clinic’s decision.",
        "enabled": "Show booking on the page",
        "enabledHint": "When off, only the call-back request form stays on the page.",
        "clinic": "Clinic",
        "clinicPlaceholder": "Choose a clinic",
        "clinicSingleHint": "The only clinic you are a member of.",
        "noClinicsTitle": "You must be a member of a clinic to offer booking",
        "noClinicsBody": "Memberships are managed under Clinics in Settings.",
        "goClinics": "Clinics",
        "mode": "Booking mode",
        "modes": { "slots": "Pick a free time", "request": "Call-back request only" },
        "modeHints": {
          "slots": "The visitor sees your free times and books after signing in to the patient app.",
          "request": "Only the call-back request form is shown."
        },
        "services": "Services",
        "servicesHint": "Only services the clinic has made public are listed. A service marked “by doctor’s referral” is shown with that badge and cannot be booked directly by a visitor.",
        "servicesEmpty": "This clinic has no public service for you yet.",
        "kinds": { "direct": "Direct booking", "referral": "By doctor’s referral" },
        "duration": "{{minutes}} min",
        "unavailable": "No longer public",
        "unavailableHint": "A service that is no longer public is removed when you save.",
        "horizon": "Days shown",
        "horizonDays": "{{days}} days",
        "horizonHint": "Free times are shown this many days ahead.",
        "previewNote": "Free times are shown on the published page.",
        "loadFailed": "Booking options could not be loaded",
        "errors": {
          "BOOKING_CLINIC_REQUIRED": "Choose a clinic.",
          "BOOKING_CLINIC_NOT_MEMBER": "You are no longer a member of this clinic; choose another one.",
          "BOOKING_SERVICES_REQUIRED": "Choose at least one service.",
          "BOOKING_SERVICE_UNAVAILABLE": "Some chosen services are no longer public and will be removed when you save."
        }
      }
    },
    "items": {
      "count": "{{count}} of {{max}}",
      "moveUp": "Move up",
      "moveDown": "Move down",
      "remove": "Remove"
    },
    "image": {
      "pick": "Choose image",
      "replace": "Change image",
      "remove": "Remove image",
      "removeTitle": "Remove the image?",
      "removeBody": "The image is removed from the page. After the next publish it is also removed from the public page.",
      "removeConfirm": "Remove",
      "cropTitle": "Crop image",
      "typeUnsupported": "Only JPEG or PNG images are accepted.",
      "tooLarge": "The image is larger than 5 MB.",
      "readFailed": "The file could not be read."
    },
    "hero": {
      "displayName": "Display name",
      "displayNameRequired": "A display name is required.",
      "headline": "Headline",
      "headlinePlaceholder": "e.g. Cardiologist, faculty member",
      "headlineHint": "In the “Modern” and “Classic” templates this is set large at the top of the page; with a comma, the part after it is shown in colour.",
      "about": "About me",
      "portrait": "Portrait",
      "portraitHint": "A separate copy of your profile picture; changing it does not affect your profile.",
      "cover": "Background image",
      "coverHint": "A wide image (about 3:1). “Modern” shows it softened behind the top of the page; “Classic” shows it as a band across the top.",
      "coverUnused": "The current template does not show a background image; choose “Modern” or “Classic” to see it.",
      "extras": {
        "onlyModern": "These two lists are shown in the “Modern” and “Classic” templates. If left empty, your first services and your council number, city and office hours take their place.",
        "notShown": "The current template does not show these two lists; choose “Modern” or “Classic” to see them.",
        "cardsTitle": "Intro cards",
        "cardsHint": "Up to two short cards at the top of the page, e.g. “In-person and online visits”.",
        "addCard": "Add card",
        "highlightsTitle": "Highlights",
        "highlightsHint": "Up to three items in the strip under the top of the page, e.g. “15 years of experience”.",
        "addHighlight": "Add highlight",
        "itemTitle": "Title",
        "itemText": "Short text (optional)",
        "titleRequired": "A title is required."
      }
    },
    "services": {
      "itemTitle": "Service title",
      "itemDescription": "Description",
      "image": "Service image",
      "imageHint": "4:3; the service title is used as the image's alternative text.",
      "imageAfterSave": "You can add an image after saving this service.",
      "add": "Add service"
    },
    "testimonials": {
      "framingTitle": "Selected testimonials, not ratings",
      "framing": "This section holds quotes you choose and enter yourself, shown under the heading “Selected patient testimonials”. No ratings, stars or review counts are shown on the page.",
      "text": "Testimonial",
      "attribution": "Patient's display name",
      "attributionPlaceholder": "e.g. “M. Rezaei” or “45-year-old patient”",
      "consent": "The patient agreed to this testimonial being published on the public page",
      "consentRequired": "This testimonial is not saved without the patient's consent confirmed.",
      "add": "Add testimonial"
    },
    "video": {
      "intro": "Paste a video address from Aparat or YouTube; only the video's id is stored.",
      "url": "Video address",
      "caption": "Short caption",
      "add": "Add video",
      "pending": "Checked when saved",
      "aparat": "Aparat",
      "youtube": "YouTube"
    },
    "gallery": {
      "alt": "Alternative text",
      "altRequired": "Alternative text is required.",
      "saveAlt": "Save text",
      "newAlt": "Description of the new image",
      "newAltPlaceholder": "e.g. “Office waiting room”",
      "altFirst": "Write the image description first.",
      "emptyTitle": "No images in the gallery yet",
      "emptyHint": "Add up to 12 images of your office and equipment."
    },
    "articles": {
      "new": "New article",
      "back": "Back to articles",
      "title": "Article title",
      "slug": "Article address (optional)",
      "slugHint": "Leave empty to build it from the title. It is fixed at the first publish.",
      "slugLocked": "Article address:",
      "body": "Article text",
      "cover": "Article image",
      "publish": "Publish article",
      "unpublish": "Return to draft",
      "republish": "Publish article changes",
      "delete": "Delete article",
      "deleteTitle": "Delete the article?",
      "deleteBody": "The article “{{title}}” will be deleted permanently.",
      "deleteConfirm": "Delete",
      "saveFirst": "Save your changes before publishing.",
      "saveToContinue": "After saving you can add an image and publish the article.",
      "untitled": "Untitled",
      "publishedOn": "Published on {{date}}",
      "neverPublished": "Not published yet",
      "statusPublished": "Published",
      "statusDraft": "Draft",
      "changed": "Unpublished changes",
      "emptyTitle": "You have not written any articles yet",
      "emptyHint": "Educational articles for patients make your page easier to find in search."
    },
    "office": {
      "intro": "What you enter here is shown on the public page exactly as written.",
      "phones": "Phones",
      "phoneLabel": "Label",
      "phoneLabelPlaceholder": "e.g. Reception",
      "phoneNumber": "Number",
      "addPhone": "Add phone",
      "city": "City",
      "address": "Office address",
      "hours": "Office hours",
      "hoursLabel": "Days",
      "hoursLabelPlaceholder": "e.g. Saturday to Wednesday",
      "hoursValue": "Hours",
      "hoursValuePlaceholder": "e.g. 16 to 20",
      "addHours": "Add hours",
      "geo": "Map location (optional)",
      "geoHint": "Mark your office on the map so patients can get directions from your page with Neshan, Balad or Google Maps.",
      "map": {
        "pick": "Choose on the map",
        "change": "Change on the map",
        "close": "Close map",
        "myLocation": "My current location",
        "clear": "Remove location",
        "isSet": "Your office is marked on the map.",
        "label": "Map for choosing the office location",
        "hint": "Tap the map or drag the pin onto your office, then press “Save”.",
        "found": "The pin is at your current location. If you are not at the office right now, move it onto the office.",
        "locating": "Finding your location…",
        "loading": "Loading the map…",
        "failed": "The map could not be loaded. If you are at the office, press “My current location”.",
        "denied": "Location access was not allowed; tap the map to mark your office.",
        "unavailable": "Your location could not be found; tap the map to mark your office.",
        "timeout": "Finding your location took too long; try again or tap the map.",
        "unsupported": "This browser cannot share a location; tap the map to mark your office."
      },
      "socials": "Social networks",
      "socialsHint": "Enter the handle or the profile address (e.g. @dr.example or instagram.com/dr.example). WhatsApp: the full number with country code. Website: the full address.",
      "social": {
        "instagram": "Instagram",
        "telegram": "Telegram",
        "whatsapp": "WhatsApp",
        "eitaa": "Eitaa",
        "bale": "Bale",
        "website": "Website"
      }
    },
    "leads": {
      "intro": "Requests visitors sent from your page. The {{brand}} team follows them up.",
      "name": "Name",
      "mobile": "Mobile",
      "preference": "Contact method and time",
      "message": "Message",
      "createdAt": "Received",
      "status": "Status",
      "more": "Show more",
      "loadFailed": "Requests could not be loaded",
      "emptyTitle": "No requests yet",
      "emptyHint": "Share your page address with patients so they can reach you through “Request an appointment”.",
      "notPublishedTitle": "The page is not published yet",
      "notPublishedHint": "Once published, visitors' requests appear here.",
      "channels": { "call": "Phone call", "sms": "SMS", "whatsapp": "WhatsApp" },
      "times": { "morning": "Morning", "afternoon": "Afternoon", "evening": "Evening", "any": "Any time" },
      "statuses": {
        "new": "New",
        "contacted": "Contacted",
        "qualified": "In progress",
        "proposal": "In progress",
        "negotiation": "In progress",
        "won": "Done",
        "lost": "Closed"
      }
    }
  }
}
